"use client";

import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, ShieldCheck, Clock, Zap, Server,
  TrendingDown, TrendingUp, Minus,
} from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';

interface KPICardProps {
  label: string;
  value: string;
  subValue?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  trendPositive?: boolean;
  icon: React.ReactNode;
  accentColor: string;
  alertState?: boolean;
  mono?: boolean;
  flashKey?: number;
}

function KPICardSkeleton() {
  return (
    <div className="cyber-card p-4 flex flex-col gap-3 relative overflow-hidden">
      {/* Background accent placeholder */}
      <div className="absolute top-0 right-0 w-20 h-20 rounded-full opacity-5 -translate-y-1/2 translate-x-1/2 bg-white/10" />

      <div className="flex items-start justify-between">
        {/* Icon skeleton */}
        <div className="w-9 h-9 rounded-lg skeleton-cyber" />
        {/* Trend badge skeleton */}
        <div className="w-16 h-5 rounded-md skeleton-cyber" />
      </div>

      <div className="flex flex-col gap-2">
        {/* Label skeleton */}
        <div className="w-20 h-2.5 rounded skeleton-cyber" />
        {/* Value skeleton */}
        <div className="w-16 h-7 rounded skeleton-cyber" />
        {/* Sub-value skeleton */}
        <div className="w-28 h-2.5 rounded skeleton-cyber" />
      </div>
    </div>
  );
}

function KPICard({
  label, value, subValue, trend, trendValue, trendPositive,
  icon, accentColor, alertState, mono, flashKey
}: KPICardProps) {
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const [animClass, setAnimClass] = useState('');
  const prevFlashKey = useRef(flashKey);

  useEffect(() => {
    if (flashKey !== undefined && flashKey !== prevFlashKey.current) {
      prevFlashKey.current = flashKey;
      setAnimClass('data-update-shimmer data-update-pulse');
      const timer = setTimeout(() => setAnimClass(''), 950);
      return () => clearTimeout(timer);
    }
  }, [flashKey]);

  return (
    <div
      className={`cyber-card hover-lift p-4 flex flex-col gap-3 relative overflow-hidden transition-all duration-200 hover:border-opacity-50 group cursor-pointer ${
        alertState ? 'border-red-500/30 bg-red-500/5' : 'hover:border-cyber-blue/20'
      } ${animClass}`}
      tabIndex={0}
      role="article"
      aria-label={`${label}: ${value}${subValue ? `, ${subValue}` : ''}${trendValue ? `, trend ${trendValue}` : ''}${alertState ? ', requires attention' : ''}`}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
    >
      {/* Background accent */}
      <div
        className="absolute top-0 right-0 w-20 h-20 rounded-full opacity-5 -translate-y-1/2 translate-x-1/2"
        style={{ background: accentColor }}
      />

      <div className="flex items-start justify-between">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${accentColor}15`, border: `1px solid ${accentColor}30` }}
        >
          <div style={{ color: accentColor }}>{icon}</div>
        </div>

        {trend && trendValue && (
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium font-mono ${
            trendPositive
              ? 'bg-green-500/10 text-green-400 border border-green-500/20' :'bg-red-500/10 text-red-400 border border-red-500/20'
          }`}>
            <TrendIcon size={10} />
            {trendValue}
          </div>
        )}
      </div>

      <div>
        <p className={`text-[11px] text-cyber-muted uppercase tracking-widest mb-1 font-mono`}>
          {label}
        </p>
        <p className={`text-2xl font-bold text-white tabular-nums ${mono ? 'font-mono' : ''}`}
          style={{ textShadow: alertState ? '0 0 10px rgba(255,59,59,0.3)' : undefined }}>
          {value}
        </p>
        {subValue && (
          <p className="text-[11px] text-cyber-muted mt-0.5 font-mono">{subValue}</p>
        )}
      </div>

      {alertState && (
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 bg-cyber-red rounded-full live-dot" />
          <span className="text-[10px] text-cyber-red font-mono">Requires attention</span>
        </div>
      )}
    </div>
  );
}

export default function KPIBentoGrid() {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [flashKeys, setFlashKeys] = useState<number[]>([0, 0, 0, 0, 0]);

  // Simulate initial data fetch
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  // Simulate data-update flashes on a staggered interval (mimics realtime updates)
  useEffect(() => {
    const interval = setInterval(() => {
      const idx = Math.floor(Math.random() * 5);
      setFlashKeys(prev => {
        const next = [...prev];
        next[idx] = Date.now();
        return next;
      });
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  const kpis = [
    {
      label: t(language, 'active_threats'),
      value: '42',
      subValue: '3 Critical · 11 High · 28 Med',
      trend: 'up' as const,
      trendValue: '+7 today',
      trendPositive: false,
      icon: <AlertTriangle size={18} />,
      accentColor: '#FF3B3B',
      alertState: true,
      mono: true,
    },
    {
      label: t(language, 'bd_compliance'),
      value: '71%',
      subValue: '18 of 25 controls passing',
      trend: 'down' as const,
      trendValue: '-3% this week',
      trendPositive: false,
      icon: <ShieldCheck size={18} />,
      accentColor: '#FFB800',
      alertState: false,
      mono: true,
    },
    {
      label: t(language, 'mttd'),
      value: '4.2h',
      subValue: 'Industry avg: 7.1h',
      trend: 'down' as const,
      trendValue: '-0.8h',
      trendPositive: true,
      icon: <Clock size={18} />,
      accentColor: '#00A3FF',
      alertState: false,
      mono: true,
    },
    {
      label: t(language, 'blocked_attacks'),
      value: '1,847',
      subValue: 'Last 24 hours · Auto-blocked',
      trend: 'up' as const,
      trendValue: '+312 vs yesterday',
      trendPositive: false,
      icon: <Zap size={18} />,
      accentColor: '#9B59FF',
      alertState: false,
      mono: true,
    },
    {
      label: t(language, 'assets_at_risk'),
      value: '23',
      subValue: '8 Servers · 12 Endpoints · 3 DBs',
      trend: 'up' as const,
      trendValue: '+5 new',
      trendPositive: false,
      icon: <Server size={18} />,
      accentColor: '#FF6B35',
      alertState: false,
      mono: true,
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-5 gap-3 h-full" aria-busy="true" aria-label="Loading KPI metrics">
        {Array.from({ length: 5 }).map((_, i) => (
          <KPICardSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-5 gap-3 h-full">
      {kpis.map((kpi, i) => (
        <KPICard key={kpi.label} {...kpi} flashKey={flashKeys[i]} />
      ))}
    </div>
  );
}